import React, { FC, useEffect, useRef, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { Alert, Box, Button, CircularProgress, Divider, Paper, Stack, TextField, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import SendIcon from '@mui/icons-material/Send';
import { useTranslation } from 'react-i18next';
import {
    postPriceRequestMessageTemplate,
    priceRequestMessagesTemplate,
    priceRequestTemplate,
} from 'apollo/templates/price-request';
import { PriceRequest, PriceRequestAction, PriceRequestMessage, PriceRequestSide } from 'models/price-request';
import { formatDate } from 'utils/format/format-date';
import { usePriceRequestUpdated } from './use-price-request-updated';
import { alwaysVisibleScrollbar } from '../always-visible-scrollbar';

type PriceRequestThreadProps = {
    priceRequestId: string;
};

const byCreatedAt = (a: PriceRequestMessage, b: PriceRequestMessage) =>
    new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();

/**
 * DD-3697 — the request's message thread (priceRequestMessages /
 * postPriceRequestMessage), styled like the order chat (pages/order-details/chat-message): a
 * card with a fixed-height scrolling list (oldest first, kept scrolled to the newest), own
 * messages on the right, and a composer below that grows when focused. Posting is offered only when the request's allowedActions include
 * POST_MESSAGE; the thread stays readable after a decision. PRICE_REQUEST_UPDATED on the
 * socket refetches messages and request.
 */
const PriceRequestThread: FC<PriceRequestThreadProps> = ({ priceRequestId }: PriceRequestThreadProps) => {
    const { t: translate } = useTranslation('price-requests');
    const [text, setText] = useState('');
    const [composerRows, setComposerRows] = useState(1);
    const listRef = useRef<HTMLDivElement>(null);

    const {
        data: requestData,
        error: requestError,
        refetch: refetchRequest,
    } = useQuery<{ priceRequest: PriceRequest }>(priceRequestTemplate, {
        variables: { id: priceRequestId },
    });
    const {
        data: messagesData,
        loading: messagesLoading,
        error: messagesError,
        refetch: refetchMessages,
    } = useQuery<{ priceRequestMessages: PriceRequestMessage[] }>(priceRequestMessagesTemplate, {
        variables: { priceRequestId },
        fetchPolicy: 'cache-and-network',
        notifyOnNetworkStatusChange: true,
    });

    usePriceRequestUpdated(priceRequestId, () => {
        refetchMessages();
        refetchRequest();
    });

    const [postMessage, { loading: posting, error: postError, reset: resetPost }] = useMutation(
        postPriceRequestMessageTemplate,
        {
            refetchQueries: [{ query: priceRequestMessagesTemplate, variables: { priceRequestId } }],
            awaitRefetchQueries: true,
            onCompleted: () => setText(''),
            // Shown below the composer; the typed text is kept for a retry.
            onError: () => undefined,
        },
    );

    const request = requestData?.priceRequest;
    const messages = [...(messagesData?.priceRequestMessages ?? [])].sort(byCreatedAt);
    const canPost = !!request?.allowedActions.includes(PriceRequestAction.POST_MESSAGE);
    const partyName = (side: PriceRequestSide) =>
        (side === PriceRequestSide.CHAIN ? request?.chain : request?.lab)?.name;

    // Keep the newest message in view, as a chat does.
    useEffect(() => {
        const list = listRef.current;
        if (list) list.scrollTop = list.scrollHeight;
    }, [messages.length]);

    const handleSend = () => {
        const trimmed = text.trim();
        if (!trimmed || posting) return;
        resetPost();
        postMessage({ variables: { priceRequestId, text: trimmed } });
    };

    const renderMessages = () => {
        if (messagesError && !messagesData) {
            return (
                <Alert
                    severity="error"
                    action={
                        <Button color="inherit" size="small" onClick={() => refetchMessages()}>
                            {translate('RETRY')}
                        </Button>
                    }
                >
                    {translate('THREAD_LOAD_FAILED')}
                </Alert>
            );
        }
        if (messagesLoading && !messagesData) {
            return (
                <Box display="flex" justifyContent="center" py={2}>
                    <CircularProgress size={24} aria-label={translate('THREAD_LOADING')} />
                </Box>
            );
        }
        if (!messages.length) {
            return <Typography color="text.secondary">{translate('THREAD_EMPTY')}</Typography>;
        }
        return (
            <Stack spacing={1.5} component="ol" sx={{ listStyle: 'none', p: 0, m: 0 }}>
                {messages.map((message) => {
                    const organizationName = partyName(message.senderSide);
                    return (
                        <Box
                            key={message._id}
                            component="li"
                            data-testid="price-request-message"
                            data-own={message.isOwn}
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: message.isOwn ? 'flex-end' : 'flex-start',
                                alignSelf: message.isOwn ? 'flex-end' : 'flex-start',
                                maxWidth: '85%',
                            }}
                        >
                            <Typography variant="caption" color="text.secondary" component="div" sx={{ px: 1 }}>
                                <strong>{message.senderName}</strong>
                                {organizationName ? ` · ${organizationName}` : ''}
                            </Typography>
                            <Box
                                sx={{
                                    px: 1.5,
                                    py: 1,
                                    borderRadius: 2,
                                    borderBottomRightRadius: message.isOwn ? 4 : undefined,
                                    borderBottomLeftRadius: message.isOwn ? undefined : 4,
                                    whiteSpace: 'pre-wrap',
                                    overflowWrap: 'anywhere',
                                    bgcolor: message.isOwn ? 'calm.dark' : 'grey.800',
                                    color: 'common.white',
                                }}
                            >
                                <Typography variant="body2">{message.text}</Typography>
                            </Box>
                            <Typography variant="caption" color="text.secondary" sx={{ px: 1, mt: 0.25 }}>
                                {formatDate(message.createdAt)}
                            </Typography>
                        </Box>
                    );
                })}
            </Stack>
        );
    };

    return (
        <Paper component="section" variant="outlined" square aria-label={translate('MESSAGES')}>
            <Box p={2}>
                <Box mb={1}>
                    <Typography variant="h3">{translate('MESSAGES')}</Typography>
                    {request && (
                        <Typography variant="caption" color="text.secondary">
                            {request.chain.name} · {request.lab.name}
                        </Typography>
                    )}
                </Box>
                <Divider />
                <Box
                    ref={listRef}
                    pt={1}
                    sx={{ height: 480, overflowY: 'auto', ...alwaysVisibleScrollbar }}
                    data-testid="price-request-messages"
                >
                    {renderMessages()}
                    {requestError && !request && (
                        <Alert severity="error" sx={{ mt: 2 }}>
                            {translate('THREAD_REQUEST_LOAD_FAILED')}
                        </Alert>
                    )}
                </Box>
                {canPost && (
                    <>
                        <Box mb={1}>
                            <Divider />
                        </Box>
                        {postError && (
                            <Alert severity="error" sx={{ mb: 1 }}>
                                {translate('MESSAGE_SEND_FAILED')}
                            </Alert>
                        )}
                        <Box mb={1}>
                            <TextField
                                id={`price-request-message-${priceRequestId}`}
                                placeholder={translate('WRITE_A_MESSAGE')}
                                inputProps={{ 'aria-label': translate('WRITE_A_MESSAGE') }}
                                value={text}
                                onChange={(event) => setText(event.target.value)}
                                onFocus={() => setComposerRows(6)}
                                onBlur={() => (!text ? setComposerRows(1) : undefined)}
                                multiline
                                rows={composerRows}
                                fullWidth
                                disabled={posting}
                            />
                        </Box>
                        <LoadingButton
                            variant="contained"
                            startIcon={<SendIcon />}
                            loading={posting}
                            disabled={!text.trim()}
                            onClick={handleSend}
                        >
                            {translate('SEND_MESSAGE')}
                        </LoadingButton>
                    </>
                )}
            </Box>
        </Paper>
    );
};

export default PriceRequestThread;
